import React, { useEffect } from "react";
import { Stage, Layer, Group } from "react-konva";
import { useRef, useState } from "react";
import FishBone from "./fishBone/FishBoneKonva";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { KonvaEventObject } from "konva/lib/Node";
import { Kbgu } from "pages/MealKBGU/kbgu/Kbgu";

interface Styles {
  container: React.CSSProperties;
}
type MealItem = {
  id: number;
  date: string;
};
const StageAny = Stage as any;
type ZoomableKonvaProps = {
  weeks: MealItem[][][];
};
function ZoomableKonva({ weeks }: ZoomableKonvaProps) {
  const [imageTransform, setImageTransform] = useState({
    scale: 1,
    position: {
      x: (1020 - 600) / 7, // Центрирование по горизонтали: (ширина Stage - ширина FishBone) / 2
      y: (700 - 650) / 7,
    }, // Центрирование по вертикали: (высота Stage - высота FishBone) / 2 },
  });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPointerPosition, setLastPointerPosition] = useState({
    x: 0,
    y: 0,
  });

  const stageRef = useRef<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBone, setSelectedBone] = useState<{
    date: string;
    mealType: string;
    dayIndex: number;
  } | null>(null);

  const [stageWidth, setStageWidth] = useState(() => {
    // Определяем ширину в зависимости от размера экрана
    if (window.innerWidth < 768) return window.innerWidth - 40; // для мобильных
    if (window.innerWidth < 1600) return 430; // для планшетов
    return 730; // для десктопов
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setStageWidth(window.innerWidth - 40);
      } else if (window.innerWidth < 1600) {
        setStageWidth(430);
      } else {
        setStageWidth(730);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "auto",
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    maxHeight: "80vh",
    overflow: "auto",
  };
  const handleBoneClick = (boneData: {
    date: string;
    mealType: string;
    dayIndex: number;
    weekIndex: number;
  }) => {
    setSelectedBone(boneData);
    setModalOpen(true);
    // console.log('Clicked bone:', boneData);
  };

  // Закрытие модального окна
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBone(null);
  };

  const handleDrug = (e: KonvaEventObject<MouseEvent>) => {
    e.evt.preventDefault();
    setIsDragging(true);
    const pos = e.target.getStage().getPointerPosition();
    setLastPointerPosition(pos);
    return;
  };

  const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    const isRightClick = e.evt.button === 2;

    if (isRightClick) {
      setIsDragging(true);
      const pos = e.target.getStage().getPointerPosition();
      setLastPointerPosition(pos);
      return;
    }

    if (e.evt.button !== 0) return;
  };

  const handleMouseMove = (e: KonvaEventObject<MouseEvent>) => {
    if (isDragging && e.evt.buttons & 2) {
      const stage = e.target.getStage();
      const currentPos = stage.getPointerPosition();
      const deltaX = currentPos.x - lastPointerPosition.x;
      const deltaY = currentPos.y - lastPointerPosition.y;

      setImageTransform((prev) => ({
        ...prev,
        position: {
          x: prev.position.x + deltaX,
          y: prev.position.y + deltaY,
        },
      }));
      setLastPointerPosition(currentPos);
      return;
    }
  };

  const handleMouseUp = (e: KonvaEventObject<MouseEvent>) => {
    if (!e.evt) return;
    const releasedButton = e.evt.button;

    if (releasedButton !== 2) {
      // Правая кнопка
      setIsDragging(false);
    }
  };

  const handleWheel = (e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const delta = e.evt.deltaY > 0 ? 0.9 : 1.1;
    // const stage = e.target.getStage();
    const stage = stageRef.current;
    const pointerPos = stage.getPointerPosition();

    if (!pointerPos) return;

    const newScale = Math.max(imageTransform.scale * delta, 1);

    const mousePointTo = {
      x: (pointerPos.x - imageTransform.position.x) / imageTransform.scale,
      y: (pointerPos.y - imageTransform.position.y) / imageTransform.scale,
    };

    const newPos = {
      x: pointerPos.x - mousePointTo.x * newScale,
      y: pointerPos.y - mousePointTo.y * newScale,
    };

    setImageTransform({
      scale: newScale,
      position: newPos,
    });
  };

  return (
    <div style={styles.container}>
      <StageAny
        ref={stageRef}
        width={stageWidth}
        height={500}
        onMouseDown={handleMouseDown}
        // onMouseMove={handleMouseMove}
        onMouseMove={(e: KonvaEventObject<MouseEvent>) => {
          handleMouseMove(e);
        }}
        onMouseUp={(e: KonvaEventObject<MouseEvent>) => handleMouseUp(e)}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
        onWheel={handleWheel}
        onContextMenu={handleDrug}
      >
        <Layer>
          <Group
            scaleX={imageTransform.scale}
            scaleY={imageTransform.scale}
            x={imageTransform.position.x}
            y={imageTransform.position.y}
          >
            <FishBone
              weeks={weeks}
              width={600 * imageTransform.scale}
              height={650 * imageTransform.scale}
              onBoneClick={handleBoneClick}
            />
          </Group>
        </Layer>
      </StageAny>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={modalStyle}>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" component="h2">
            Детали приема пищи
          </Typography>

          {selectedBone && (
            <>
              <Typography sx={{ mt: 2 }}>Дата: {selectedBone.date}</Typography>
              <Kbgu />
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
}

const styles: Styles = {
  container: {
    borderRadius: "15px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "var(--bcg)",
  },
};

export default ZoomableKonva;
